import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useAuthenticatedFetchAllPages } from "../../lib/pagination";
import type { HouseholdCustomFieldDefinition } from "./customFieldFilter";

/**
 * The path of `HouseholdsController.getHouseholdCustomFields`. Also the FIRST
 * element of every react-query key of the page walk.
 */
export const HOUSEHOLD_CUSTOM_FIELDS_PATH = "/v1/households/custom-fields";

/**
 * The household custom fields of the firm's CRM, for the "Custom fields" item
 * (spec `doc-custom-field-filtering-01a11cc6e668eb59`, D40). The route answers
 * a page, so this walks every page, as `useQueryContactTags` (`filters.tsx`)
 * walks `GET /v1/contacts/tags`, and holds `data` back until the last page is
 * in.
 *
 * The cached pages are dropped when the hook unmounts, as in
 * `useQueryContactTags`: the definitions are read from the CRM, and after a
 * failed definitions read the next open of the table reads them again (R34).
 */
export function useQueryHouseholdCustomFields() {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError } =
    useAuthenticatedFetchAllPages<HouseholdCustomFieldDefinition>(
      HOUSEHOLD_CUSTOM_FIELDS_PATH,
    );

  useEffect(
    () => () =>
      queryClient.removeQueries({ queryKey: [HOUSEHOLD_CUSTOM_FIELDS_PATH] }),
    [queryClient],
  );

  const data = useMemo(
    () => (hasEveryRow ? rows : undefined),
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isError, data };
}
